import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SlidersHorizontalIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { ColorOverride, FieldDefinition, FieldSpan, HighlightTone, LayoutSettings, LoanRecord, PanelField, RuleMatch, ToneColors } from '../../types/highlights';
import { neutralChip, styleForTone, toneLabels, toneOrder } from '../../utils/palette';
import { formatSpan, resolveSpan, spanOptions, textScaleStyles, toGridTracks, truncateValue } from '../../utils/layout';
interface HighlightChipProps {
  definition: FieldDefinition;
  field: PanelField;
  loan: LoanRecord;
  index: number;
  total: number;
  toneColors: ToneColors;
  layout: LayoutSettings;
  match: RuleMatch | null;
  ruleCount: number;
  editable: boolean;
  /** Total grid tracks in a row, used to clamp wide fields. */
  totalTracks: number;
  isDragTarget: boolean;
  onDragStartChip: (index: number) => void;
  onDragOverChip: (index: number) => void;
  onDropChip: (index: number) => void;
  onDragEndChip: () => void;
  onMove: (from: number, to: number) => void;
  onRemove: (fieldId: string) => void;
  onSetColorOverride: (fieldId: string, colorOverride?: ColorOverride) => void;
  onSetSpan: (fieldId: string, span: FieldSpan) => void;
  onSetMaxChars: (fieldId: string, maxChars?: number) => void;
  onManageRules: (fieldId: string) => void;
}
export function HighlightChip({
  definition,
  field,
  loan,
  index,
  total,
  toneColors,
  layout,
  match,
  ruleCount,
  editable,
  totalTracks,
  isDragTarget,
  onDragStartChip,
  onDragOverChip,
  onDropChip,
  onDragEndChip,
  onMove,
  onRemove,
  onSetColorOverride,
  onSetSpan,
  onSetMaxChars,
  onManageRules
}: HighlightChipProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);
  const {
    colorOverride,
    span,
    maxChars
  } = field;
  const tone: HighlightTone | null = colorOverride === 'none' ? null : colorOverride ? colorOverride : match ? match.tone : null;
  const styles = tone ? styleForTone(tone, toneColors) : neutralChip;
  const scale = textScaleStyles[layout.textScale];
  const fullValue = definition.getValue(loan);
  const displayValue = truncateValue(fullValue, maxChars);
  const isEmpty = fullValue === '—';
  const showFlag = Boolean(match?.flag) && !colorOverride && tone !== null;
  const columns = resolveSpan(span, fullValue.length, layout);
  const tracks = toGridTracks(columns, totalTracks);
  const chipContent = <>
      <span className="flex min-w-0 flex-1 flex-col gap-[3px] leading-tight" data-id="element-22" data-mp="3ltfaaqloj7m4">
            <span className={['truncate font-semibold uppercase tracking-wide', scale.labelText, tone ? styles.text : 'text-slate-500'].join(' ')} data-id="element-23" data-mp="3lvs17t9f0sbb">
              {definition.label}
            </span>
            <span className={['truncate font-bold', scale.valueText, isEmpty ? 'font-semibold text-slate-400' : styles.text].join(' ')} data-id="element-24" data-mp="4j7g2dmguto2">
              {displayValue}
            </span>
          </span>
          {showFlag && match?.flag ? <span className={`flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-px font-bold ${scale.flagText} ${styles.flag}`} data-id="element-25" data-mp="nbjp1j7mkt76">
              <TriangleAlertIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-26" data-mp="9mzvmmt451en" />
              {match.flag}
            </span> : null}
    </>;
  const chipBaseClass = ['flex w-full items-center gap-1.5 whitespace-nowrap rounded border transition', scale.height, scale.padding, styles.chip].join(' ');
  if (!editable) {
    return <div className="relative min-w-0" style={{
      gridColumn: `span ${tracks} / span ${tracks}`
    }} data-id="element-27" data-mp="3mwntvzjhohs5">
        <div className={chipBaseClass} title={match?.reason ?? fullValue} data-id="element-28" data-mp="3hy2shwsi3s24">
          <span className="flex min-w-0 flex-1 items-center gap-2" data-id="element-29" data-mp="pfrn63v8r0c">{chipContent}</span>
        </div>
        {match ? <span className="sr-only" data-id="element-30" data-mp="3rtwdm4kactvi">{match.reason}</span> : null}
      </div>;
  }
  return <div ref={containerRef} className="relative min-w-0" style={{
    gridColumn: `span ${tracks} / span ${tracks}`
  }} data-id="element-31" data-mp="1c2i69vmen6hv">
      <div draggable onDragStart={event => {
      event.dataTransfer.effectAllowed = 'move';
      onDragStartChip(index);
    }} onDragOver={event => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      onDragOverChip(index);
    }} onDrop={event => {
      event.preventDefault();
      onDropChip(index);
    }} onDragEnd={onDragEndChip} className={[chipBaseClass, 'cursor-grab active:cursor-grabbing', isDragTarget ? 'ring-2 ring-[#0b5cab] ring-offset-1' : ''].join(' ')} data-id="element-32" data-mp="176kw671qiwdj">
        <button type="button" onClick={() => setMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={menuOpen} aria-label={`${definition.label}: ${fullValue}. Field options`} title={match?.reason ?? fullValue} className="flex min-w-0 flex-1 items-center gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]" data-id="element-33" data-mp="1omypdtgm6eg8">
          {chipContent}
        </button>

        <button type="button" onClick={() => onRemove(definition.id)} aria-label={`Remove ${definition.label} from highlights`} className={['ml-0.5 shrink-0 border-l pl-1.5 text-slate-400 transition hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', styles.divider].join(' ')} data-id="element-34" data-mp="2qyq6521l8igl">
          <XIcon className="h-3 w-3" data-id="element-35" data-mp="279xuv7ynri1h" />
        </button>
      </div>

      {match ? <span className="sr-only" data-id="element-36" data-mp="238vs4h5j8nur">{match.reason}</span> : null}

      {menuOpen ? <div role="menu" aria-label={`${definition.label} settings`} className="absolute left-0 top-[calc(100%+6px)] z-30 w-64 rounded-md border border-slate-200 bg-white p-2 text-left shadow-lg" data-id="element-37" data-mp="236rrq96ue6z8">
          <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-38" data-mp="1ob3fbwrdedk9">
            Highlight
          </p>
          <div className="flex items-center gap-1.5 px-1 pb-2" data-id="element-39" data-mp="1e754aoemcizz">
            {toneOrder.map(toneOption => {
          const active = colorOverride === toneOption;
          return <button key={toneOption} type="button" role="menuitemradio" aria-checked={active} aria-label={toneLabels[toneOption]} title={toneLabels[toneOption]} onClick={() => onSetColorOverride(definition.id, toneOption)} className={['h-6 w-6 rounded border-2 transition', styleForTone(toneOption, toneColors).swatch, active ? 'border-slate-900' : 'border-white hover:border-slate-300'].join(' ')} data-id="element-40" data-mp="1o75oeckbb358" />;
        })}
            <button type="button" role="menuitemradio" aria-checked={colorOverride === 'none'} aria-label="No highlight" title="No highlight" onClick={() => onSetColorOverride(definition.id, 'none')} className={['h-6 w-6 rounded border-2 bg-white transition', colorOverride === 'none' ? 'border-slate-900' : 'border-slate-200 hover:border-slate-400'].join(' ')} data-id="element-41" data-mp="hoz26oax9d1c" />
          </div>

          {colorOverride !== undefined ? <button type="button" role="menuitem" onClick={() => onSetColorOverride(definition.id, undefined)} className="mb-1 w-full rounded px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50" data-id="element-42" data-mp="2asq4tj1d7xii">
              Clear manual color
            </button> : null}

          <div className="my-1 border-t border-slate-100" data-id="element-43" data-mp="2tjzb7jfua65v" />

          <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-44" data-mp="2tlu018u8eif8">
            Rules
          </p>
          <p className="px-1 pb-1.5 text-[11px] leading-snug text-slate-500" data-id="element-45" data-mp="3h7e28csbjnk6">
            {ruleCount === 0 ? 'No rules on this field yet.' : match ? `Matching: ${match.reason}` : `${ruleCount} rule${ruleCount === 1 ? '' : 's'}, none matching now.`}
          </p>
          <button type="button" role="menuitem" onClick={() => {
        setMenuOpen(false);
        onManageRules(definition.id);
      }} className="mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] font-medium text-[#0b5cab] hover:bg-[#f3f8ff]" data-id="element-46" data-mp="2tha4gm1tai13">
            <SlidersHorizontalIcon className="h-3.5 w-3.5" data-id="element-47" data-mp="2pe3y7eep8qum" />
            {ruleCount === 0 ? 'Create a rule' : 'Edit rules'}
          </button>

          <div className="my-1 border-t border-slate-100" data-id="element-48" data-mp="2fik2ot41ru6e" />

          <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-49" data-mp="2fk51iuxe9za2">
            Width
          </p>
          <div role="radiogroup" aria-label="Field width" className="grid grid-cols-4 gap-1 px-1 pb-1" data-id="element-50" data-mp="3afmhvs8goqsc">
            {spanOptions.map(option => {
          const active = (span ?? 'auto') === option.value && !layout.uniformSizing;
          return <button key={String(option.value)} type="button" role="radio" aria-checked={active} disabled={layout.uniformSizing} onClick={() => onSetSpan(definition.id, option.value)} className={['rounded border px-1 py-1 text-[12px] font-medium transition', active ? 'border-[#0b5cab] bg-[#f3f8ff] text-[#0b5cab]' : 'border-slate-200 text-slate-600 hover:bg-slate-50', layout.uniformSizing ? 'cursor-not-allowed opacity-50' : ''].join(' ')} data-id="element-51" data-mp="3o682pmhh7asv">
                  {option.label}
                </button>;
        })}
          </div>
          <p className="px-1 pb-2 text-[11px] leading-snug text-slate-500" data-id="element-52" data-mp="2a5lffae6sjk6">
            {layout.uniformSizing ? 'Uniform sizing is on — turn it off under Configure to set widths.' : (span ?? 'auto') === 'auto' ? `Auto fits the value (${fullValue.length} characters → ${formatSpan(columns)}×).` : `Currently ${formatSpan(columns)}× of the base column width.`}
          </p>

          <label htmlFor={`maxchars-${definition.id}`} className="block px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-53" data-mp="1qgt4xij2zn64">
            Max characters
          </label>
          <div className="flex items-center gap-2 px-1 pb-1" data-id="element-54" data-mp="2xwr3gcua6i5k">
            <input id={`maxchars-${definition.id}`} type="number" min={4} max={80} value={maxChars ?? ''} placeholder="Full value" onChange={event => {
          const next = Number(event.target.value);
          onSetMaxChars(definition.id, event.target.value === '' || Number.isNaN(next) ? undefined : next);
        }} className="w-24 rounded border border-slate-200 px-2 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-55" data-mp="2xyk2p72ke03j" />
            <button type="button" disabled={maxChars === undefined} onClick={() => onSetMaxChars(definition.id, undefined)} className="rounded px-1.5 py-1 text-[12px] font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-56" data-mp="1kvicxuzc2knm">
              Clear
            </button>
          </div>
          <p className="px-1 pb-2 text-[11px] leading-snug text-slate-500" data-id="element-57" data-mp="pmwyne2j9022">
            Value is {fullValue.length} characters long.
          </p>

          <div className="my-1 border-t border-slate-100" data-id="element-58" data-mp="klhave8z0ffz" />

          <button type="button" role="menuitem" disabled={index === 0} onClick={() => onMove(index, index - 1)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-59" data-mp="gkxg2hbvdnx0">
            <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-60" data-mp="3t3dfg7kw99z4" /> Move left
          </button>
          <button type="button" role="menuitem" disabled={index === total - 1} onClick={() => onMove(index, index + 1)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300" data-id="element-61" data-mp="3t1atbymv40zo">
            <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-62" data-mp="1j1ivzqptnk7l" /> Move right
          </button>
        </div> : null}
    </div>;
}